import { Network, Token } from 'config/types'
import { useMemo } from 'react'
import { useSelector } from 'react-redux'
import { AppState } from 'state'
import { useAllTokens, useListNetworks } from 'state/app/hooks'

const useAvailableTokens = () => {
  const tokenList = useAllTokens((_token, _, _array, info) => {
    return !(info.disabledDeposit && info.disabledWithdrawal && info.disabledPlay)
  })
  const tokenPriorities = useSelector((state: AppState) => state.app.tokenPriorities)
  const networkList = useListNetworks()

  const tokensSortbyPriority = useMemo(() => {
    if (!tokenPriorities) return tokenList

    const networkPriorityMap = networkList.reduce((map, net, index) => {
      map[net.chainId] = net.order ?? index
      return map
    }, {} as Record<number, number>)

    const tokenSortByNetwork = tokenList.sort((token1, token2) => {
      const netPriority1 = networkPriorityMap[token1.network] ?? Infinity
      const netPriority2 = networkPriorityMap[token2.network] ?? Infinity
      return netPriority1 - netPriority2
    })

    return tokenSortByNetwork.sort((token1, token2) => {
      const priority1 = tokenPriorities[token1.code] ?? 0
      const priority2 = tokenPriorities[token2.code] ?? 0
      return priority1 - priority2
    })
  }, [tokenList, tokenPriorities, networkList])

  const currenciesGroupSameCode = useMemo(() => {
    const groupCurrenciesMap: Record<string, boolean> = {}
    const groupCurrencies: Token[] = []

    const networksCurrencySameCode: Record<string, Network[]> = {}

    tokensSortbyPriority.forEach((token) => {
      const uniqueKey = `${token.code}_${token.logo}`
      if (!groupCurrenciesMap[uniqueKey]) {
        groupCurrenciesMap[uniqueKey] = true
        groupCurrencies.push(token)
      }

      if (!networksCurrencySameCode[token.code]) {
        networksCurrencySameCode[token.code] = []
      }

      const networkInfo = networkList.find((n) => n.chainId === token.network)
      if (networkInfo && !networksCurrencySameCode[token.code].some((n) => n.chainId === networkInfo.chainId)) {
        networksCurrencySameCode[token.code].push(networkInfo)
      }
    })

    return groupCurrencies
  }, [tokensSortbyPriority])

  return currenciesGroupSameCode
}

export default useAvailableTokens
